import { fromJS } from 'immutable';

import { composeFiltersUiShape } from '../filters';
import {
  initiate,
  initiatePaths,
  initiateState
} from './initiate';
import {
  commit,
  commitPaths,
  commitState
} from './commit';
import {
  carClassState,
  selectCarClass,
  upgradeCarClass
} from './carClass';
import {
  extrasState,
  extrasUpdate
} from './extras';
import {
  driverProfileModify,
  driverProfileState,
  searchProfileByDriverLicense,
  searchRenterByMembership
} from './driverProfile';
import { checkRentalExtendEligibility, initiateRentalExtend, commitRentalExtend, clearExtendRentalState } from './extendRental';
import { initiateEmeraldCheckout, selectEmeraldCheckoutVehicle, updateEmeraldCheckoutProducts, commitEmeraldCheckout, clearEmeraldCheckoutState, updateEmeraldCheckoutPaymentMethod, listEmeraldCheckoutVehicle, cancelEmeraldCheckoutVehicle } from './emeraldCheckout';
import request from '../request';
import reservations from '../../services/gma/reservations';
import { CLEAN, STATE } from '../../constants';
import { isArrayNotEmpty } from '../../utils/js/array';
import { get } from '../../utils/js/object';
import {
  getSelectedExtrasFromReservation,
  getVehicleSelectLabelByCode
} from '../../utils/app/reservations';
import { types } from '../../utils/js/types';

/**
 * @namespace "actions.reservations"
 */

/**
 * @name cleanNonSessionReservationState
 * @desc Removes data from store not associated with session.gbo.reservation; i.e. gma.reservation.carClassDetails
 * @param {Object} [opts] {opts: Object}
 * @returns {{type: string, cleanState: Object}}
 * @memberof actions.reservations
 * @example
 * // if opts.cleanCarClassState === true, restore state.gmi.ui.reservations.car_class to initialState
 * // if opts.cleanCommitState === true, restore state.gmi.ui.reservations.commit to initialState
 * // if opts.cleanExtrasState === true, restore state.gmi.ui.reservations.extras to initialState
 * // if opts.cleanGmaReservationState === true, restore state.gmi.gma.reservation to initialState
 * // if opts.cleanInitiateState === true, restore state.gmi.ui.reservations.initiate to initialState
 * // if opts.cleanVehicleFilters === true, restore state.gmi.filters.car_classes to initialState
 * // if opts.cleanDriverProfile === true, restore state.gmi.reservations.driverProfile to initialState
 * dispatch(actions.reservations.cleanNonSessionReservationState());
 */
export function cleanNonSessionReservationState(opts = {}) {
  const cleanState = Object.assign({
    cleanCarClassState: true,
    cleanCommitState: true,
    cleanExtrasState: true,
    cleanGmaReservationState: true,
    cleanInitiateState: true,
    cleanVehicleFilters: true,
    cleanDriverProfile: true
  }, opts);
  return { type: CLEAN.CLEAN_RESERVATION_STATE, cleanState };
}

/**
 * @private
 * @name getVehicleSelectCarClasses
 * @desc Get car_classes array with a previously_selected or unavailable car class at the top of the array
 * @param {Object} reservation
 * @param {Object} [opts] {pull_to_top:String, show_unavailable:Boolean}
 * @returns {Array}
 * // Modify: pull_to_top will negate any vehicle on the reservation with previously_selected === true from being sent to the top of the array
 */
export function getVehicleSelectCarClasses(reservation, opts = {}) {
  const { pull_to_top, show_unavailable = true } = opts;
  const previously_selected = { previously_selected: true };
  const next_car_classes = (show_unavailable && get(reservation, 'modification_constraints.original_car_class_unavailable')) ?
    [Object.assign(reservation.car_class_details, previously_selected)] : [];
  return next_car_classes.concat(reservation.car_classes.reduce((arr, item) => {
    if ((!pull_to_top && item.previously_selected) || item.code === pull_to_top) {
      if (!item.previously_selected) Object.assign(item, previously_selected);
      arr.unshift(item);
    } else {
      arr.push(item);
    }
    return arr;
  }, []));
}

/**
 * @private
 * @name getVehicleSelectFiltersArr
 * @desc Using an array of filter_codes, returns an array of related filter {category, keyPath} objects
 * @param {Array} filter_codes i.e. ['CLASS', 'TRANSMISSION', 'PASSENGERS']
 * @param {Array} car_classes_filters
 * @returns {Array}
 */
export function getVehicleSelectFiltersArr(filter_codes, car_classes_filters) {
  const filters = new Array(filter_codes.length);
  car_classes_filters.forEach(filter => {
    const i = filter_codes.indexOf(filter.filter_code);
    if (i !== -1) filters[i] = { category: filter.filter_description, keyPath: `filters.${filter.filter_code}.filter_code` };
  });
  return filters;
}

/**
 * @name initVehicleSelectFilters
 * @desc Creates a UI filter shape from an array of desired car class filter codes
 * @param {Object} [config] {filter_codes:Array, segments:Array, show_unavailable:bool}
 * @returns {Function}
 * @memberof actions.reservations
 * @example
 * // below is a default value and does not need to be included
 * const filter_codes = ['CLASS', 'TRANSMISSION', 'PASSENGERS'];
 * // if you include a `previously_selected` car class code, that car class will be unshifted to the top of
 * // the array and given the `previously_selected: true` flag. this should be used if you need to
 * // highlight a car class when car_class_details does not exist, or to bring a car to top during inflight modify
 * const previously_selected = 'CCAR';
 * // if you include `show_unavailable` and set it to false, the previously selected car_class will NOT be
 * // shown if it is no longer available
 * const show_unavailable = false;
 * // finally, you can add segments to filter with OR logic ahead of the traditional car class filters
 * // also, if you include segments, GMI will automatically add `{keyPath:'previously_selected', values:true}` as a segment
 * // to ensure those items do not get hidden by default
 * const segments = [{keyPath:'status', values:'VEHICLE_AVAILABLE_AT_PROMOTIONAL_RATE'}]};
 * dispatch(actions.reservations.initVehicleSelectFilters({filter_codes, previously_selected, segments, show_unavailable});
 */
function initVehicleSelectFilters(config = {}) {
  const { filter_codes = ['CLASS', 'TRANSMISSION', 'PASSENGERS'], previously_selected, segments = [], show_unavailable } = config;
  // if using segments, add previously selected to the top
  // this will be moot if item is unavailable and show_unavailable === true
  if (isArrayNotEmpty(segments)) segments.unshift({ keyPath: 'previously_selected', values: true });
  return (dispatch, getState) => {
    const reservation = getState().getIn(STATE.SESSION.concat(['gbo', 'reservation'])).toJS();
    const { car_classes_filters } = reservation;
    let filters = [], labelFn;
    if (isArrayNotEmpty(car_classes_filters)) {
      labelFn = code => getVehicleSelectLabelByCode(car_classes_filters, code);
      filters = getVehicleSelectFiltersArr(filter_codes, car_classes_filters);
    }
    dispatch(composeFiltersUiShape({
      items: getVehicleSelectCarClasses(reservation, { pull_to_top: previously_selected, show_unavailable }),
      storeKey: 'car_classes',
      filters,
      labelFn,
      segments
    }));
  };
}

/**
 * @name modifyAbort
 * @desc Only use if after GMA modifyAbort service, your app should dispatch cleanNonSessionReservationState. Else, just dispatch request({service: reservations.modifyAbort})
 * @param {Object} [data] {params}
 * @returns {Function}
 * @memberof actions.reservations
 * @example
 * dispatch(actions.reservations.modifyAbort());
 * // will clean...
 * // state.gmi.ui.reservations
 * // state.gmi.gma.reservations
 * // state.gmi.session.gma.modify === false
 */
function modifyAbort(data = {}, requestOpts = {}) {
  return dispatch => dispatch(request({
    data,
    service: reservations.modifyAbort,
    opts: requestOpts
  }))
    .then(resp => {
      if (!isArrayNotEmpty(resp.messages)) dispatch(cleanNonSessionReservationState());
      return resp;
    });
}

/**
 * @name modifyStart
 * @desc Only use if after GMA modifyStart service, your app should populate initiate/commit state from gbo.reservation. Else, just dispatch request({service: reservations.modifyStart})
 * @param {Object} [data] {params}
 * @param {Object} [requestOpts]
 * @returns {Function}
 * @memberof actions.reservations
 * @example
 * dispatch(actions.reservations.modifyStart());
 * // will hydrateUiReservationsState
 */
function modifyStart(data = {}, requestOpts = {}) {
  return (dispatch) => dispatch(request({ data, service: reservations.modifyStart, opts: requestOpts }))
    .then(resp => {
      if (!isArrayNotEmpty(resp.messages)) {
        return dispatch(hydrateUiReservationsState({ initiateFromGma: true })).then(() => resp);
      }
      return resp;
    });
}

/**
 * @name oneClickModify
 * @desc Starts modify in a one click reservation.
 * @param {String} stepToModify The type of modification the user wants to do
 * @param {Object} [requestOpts]
 * @returns {Object}
 * @memberof actions.reservation
 * @example
 * dispatch(actions.reservations.oneClickModify('extras'));
 */
export function oneClickModify(stepToModify, requestOpts = {}) {

  return (dispatch) => {
    const payload = {
      stepToModify
    };

    return dispatch(request({
      data: { payload },
      service: reservations.oneClickModify,
      opts: requestOpts
    }));
  };
}


/**
 * @private
 * @name setUiReservationsState
 * @param {Object} [opts] {initiateState:Object, carClassState:Object, extrasState:Object, commitState:Object, profile: Object}
 * @desc A setter for all reservation payload data and for reservation driver profile if profile param exists
 * @returns {Function}
 */
function setUiReservationsState(opts = {}) {
  return dispatch => dispatch(initiateState.set(null, opts.initiateState))
    .then(resp => {
      if (!resp.messages) {
        if (opts.carClassState) dispatch(carClassState.set(null, opts.carClassState));
        if (opts.extrasState) dispatch(extrasState.set(null, opts.extrasState));
        if (opts.commitState) dispatch(commitState.set(null, opts.commitState, { format: false }));
        if (opts.loyaltyProfile) {
          dispatch(driverProfileState.set(null, opts.loyaltyProfile));
        }
      }
      return resp;
    });
}

/**
 * @private
 * @desc prepares InitiateState from appropriate part of session data to be saved to UI state
 * @param {Immutable} session
 * @param {Boolean} initiateFromGma
 * @returns {Object}
 */
function getInitiateState(session, initiateFromGma) {
  const gmaInitiateRequest = session.getIn(['gma', 'reservation', 'initiate_request']) ||
    session.getIn(['gma', 'initiate_request']) ||
    session.getIn(['gma', 'deep_link_reservation', 'reservation']); // fallback for eweb deeplinks
  let reservation = initiateFromGma ? (
    gmaInitiateRequest
  ) : (
    session.getIn(['gbo', 'reservation']) ||
    gmaInitiateRequest ||
    session.getIn(['gma', 'pre_selected', 'reservation'])
  );

  if (reservation) {
    const product_code = gmaInitiateRequest ? gmaInitiateRequest.get('product_code', null) : null;
    const renter_age_label = gmaInitiateRequest ? gmaInitiateRequest.get('renter_age_label', null) : null;
    const preferred_car_class_codes = gmaInitiateRequest ? gmaInitiateRequest.get('preferred_car_class_codes', null) : null;
    const preferred_car_category_code = gmaInitiateRequest ? gmaInitiateRequest.get('preferred_car_category_code', null) : null;
    reservation = reservation.toJS();
    const coupons = isArrayNotEmpty(reservation.coupons)
      ? reservation.coupons.map(coupon => types(coupon).isObject ? coupon.code : coupon)
      : null;


    return {
      booking_agent: reservation.booking_agent,
      car_class_code: get(reservation, 'car_class_details.code') || '',
      contract_number: reservation.contract_number,
      coupons,
      product_code,
      one_way_rental: reservation.one_way_rental,
      pickup_location: reservation.pickup_location,
      pickup_time: reservation.pickup_time,
      preferred_car_class_codes,
      preferred_car_category_code,
      renter_age: reservation.renter_age,
      renter_age_label,
      return_location: reservation.return_location,
      return_time: reservation.return_time,
      additional_information: reservation.additional_information,
      bundle_profile: reservation.bundle_profile
    };
  } else {
    // ECR-29874 :: if we do not return an object we cannot destructure in the later steps
    return {};
  }
}

/**
 * @private
 * @desc prepares carClassState from appropriate part of session data to be saved to UI state
 * @param {Immutable} session
 * @returns {Object}
 */
function getCarClassState(session) {
  const gbo = session.getIn(['gbo', 'reservation', 'car_class_details']);
  const pre = session.getIn(['gma', 'pre_selected', 'reservation', 'car_class_details']);
  return (gbo || pre) ? (gbo || pre).toJS() : null;
}

/**
 * @private
 * @desc prepares extrasState from appropriate part of session data to be saved to UI state
 * @param {Immutable} session
 * @returns {Array}
 */
// TODO: needs to be tested with pre_selected values
function getExtrasState(session) {
  let extras;
  const gbo = session.getIn(['gbo', 'reservation']);
  extras = gbo ? getSelectedExtrasFromReservation({ reservation: gbo.toJS() }) : [];

  if (!extras.length) {
    const pre = session.getIn(['gma', 'pre_selected', 'reservation']);
    extras = pre ? getSelectedExtrasFromReservation({ reservation: pre.toJS() }) : [];
  }

  return extras.length ? extras : null;
}



/**
 * @private
 * @desc destructure reservation commit data
 * @param {Immutable.Map} session
 * @param {Immutable.Map} state
 * @param {boolean} mergeCommitAdditionalInformation
 * @returns {Object}
 */
// TODO: needs to be tested with pre_selected values
export function getCommitState(session, state, mergeCommitAdditionalInformation = false) {
  function destructureCommitParamsFromReservation(reservation) {
    const {
      additional_information,
      airline_info = {},
      billing_account = {},
      driver_info = {},
      selected_payment_method = {}
    } = reservation.toJS();

    const {
      code,
      flight_number
    } = airline_info;

    const {
      email_address,
      first_name,
      last_name,
      phone,
      request_email_promotions
    } = driver_info;

    // This double value is temporary! GBO is transitioning from using `payment_ids` to `payment_method` in the
    // meanwhile we are supporting both...
    // This is the legacy pattern for passing along payment ID
    const payment_ids = selected_payment_method.payment_session_id ? [selected_payment_method.payment_session_id] : null;
    // This is the "new" pattern proposed moving forward
    const payment_method = selected_payment_method.payment_session_id ? { payment_session_id: selected_payment_method.payment_session_id } : null;


    /**
     * On EWeb's resflow, due to the session needing to be refreshed in the middle of the commit step
     * on Expedite, we need to retain the existing UI form values to make sure
     * the user doesn't lose any inputed information.
     * We're doing this as an opt-in behavior as we don't want to introduce breaking changes
     * to other brands in this version.
     * 
     * @todo We've discussed how to make this more generic for a future GMI-next version.
     * We want to have some generic, opt-in logic for persisting UI values over server values,
     * but also need to have some way to override that logic on a case-by-case scenario.
     */
    if (mergeCommitAdditionalInformation) {
      const uiAdditionalInfo = state.getIn(commitPaths.additionalInfo)?.toJS();

      uiAdditionalInfo?.forEach(uiItem => {
        if (!uiItem.value) return;

        const sessionFieldIndex = additional_information.findIndex(sessionItem => sessionItem.id === uiItem.id);

        if (sessionFieldIndex === -1) return;

        additional_information[sessionFieldIndex] = uiItem;
      });
    }

    const commitState = {
      additional_information,
      billing_account: billing_account.billing_account_number,
      billing_account_type: billing_account.billing_account_type,
      code,
      email_address,
      first_name,
      flight_number,
      last_name,
      payment_ids,
      phone,
      request_email_promotions
    };

    if (payment_method) {
      commitState.payment_method = payment_method;
    }

    return commitState;
  }

  let gbo = session.getIn(['gbo', 'reservation']);
  gbo = gbo ? destructureCommitParamsFromReservation(gbo) : {};
  let pre = session.getIn(['gma', 'pre_selected', 'reservation']);
  pre = pre ? destructureCommitParamsFromReservation(pre) : {};

  const commitState = Object.assign(pre, gbo);
  return Object.keys(commitState).length ? commitState : null;
}

/**
 * @private
 * @name getProfileForLoyaltyReservation
 * @param {Immutable} session
 * @desc Checks if a logged in user and a reservation loyalty number match and returns that users profile
 * @returns {Object | null}
 */
function getProfileForLoyaltyReservation(session) {
  // TODO: Can profile_details come from pre_selected values?
  let reservation = session.getIn(['gbo', 'reservation']);
  let profile = session.getIn(['gbo', 'profile']);

  if (reservation && profile) {
    reservation = reservation.toJS();
    profile = profile.toJS();
    const resLoyaltyId = get(reservation, 'profile_details.loyalty_data.loyalty_number');
    const userLoyaltyId = get(profile, 'profile.basic_profile.loyalty_data.loyalty_number');
    if (resLoyaltyId && userLoyaltyId && resLoyaltyId === userLoyaltyId) {
      return profile;
    }
  }

  return null;
}

/**
 * @name hydrateUiReservationsState
 * @desc hydrates gmi.ui source of truth state from session
 * @param {{initiateFromGma:boolean, mergeCommitAdditionalInformation: boolean, actionSession: object}} [opts] 
 * @returns {Function}
 * @memberof actions.reservations
 * @example
 * dispatch(actions.reservations.hydrateUiReservationsState());
 * // will populate...
 * // state.gmi.ui.reservations.initiate
 * // state.gmi.ui.reservations.car_class
 * // state.gmi.ui.reservations.extras
 * // state.gmi.ui.reservations.commit
 * // state.gmi.session.gma.modify === true
 * // if reservation and profile loyalty numbers match, it will
 * // also populate state.gmi.ui.reservations.driver_profile
 */
export function hydrateUiReservationsState(opts = {}) {
  const { initiateFromGma, mergeCommitAdditionalInformation, actionSession } = opts;
  return (dispatch, getState) => {
    const state = getState();
    const session = fromJS(actionSession) || state.getIn(STATE.SESSION);
    const initiateState = getInitiateState(session, initiateFromGma);
    const carClassState = getCarClassState(session);
    const extrasState = getExtrasState(session);
    const commitState = getCommitState(session, state, mergeCommitAdditionalInformation);
    const loyaltyProfile = getProfileForLoyaltyReservation(session);

    return dispatch(setUiReservationsState({ initiateState, carClassState, extrasState, commitState, loyaltyProfile }));
  };
}

// the following is imported in actions/index.js as "reservations"
export default {
  cleanNonSessionReservationState,
  commit,
  extrasUpdate,
  initiate,
  initVehicleSelectFilters,
  modifyAbort,
  oneClickModify,
  modifyStart,
  selectCarClass,
  upgradeCarClass,
  searchProfileByDriverLicense,
  searchRenterByMembership,
  driverProfileModify,
  hydrateUiReservationsState,
  checkRentalExtendEligibility,
  initiateRentalExtend,
  commitRentalExtend,
  clearExtendRentalState,
  initiateEmeraldCheckout,
  selectEmeraldCheckoutVehicle,
  updateEmeraldCheckoutProducts,
  updateEmeraldCheckoutPaymentMethod,
  commitEmeraldCheckout,
  clearEmeraldCheckoutState,
  listEmeraldCheckoutVehicle,
  cancelEmeraldCheckoutVehicle,
  /**
   * @namespace "actions.reservations.initiateState"
   */
  initiateState,
  /**
   * @namespace "actions.reservations.carClassState"
   */
  carClassState,
  /**
   * @namespace "actions.reservations.extrasState"
   */
  extrasState,
  /**
   * @namespace "actions.reservations.commitState"
   */
  commitState,
  /**
   * @namespace "actions.reservations.driverProfileState"
   */
  driverProfileState
};
